// Oike — what a request is for: the kind of form (team, individual, webinar),
// its topic and, for a webinar, its event. Shared by the public form's
// functions (request.mts, request-form.mts) and the Solicitudes screens. Pure.
//
// The kind and the topic come from the identified link (table Source); without
// a link, from the URL (oike_form, oike_topic), and otherwise the team form,
// as before. They describe the request, never where it came from: the origin
// stays in src/requestOrigin.js. Topics are a fixed list Ale keeps in the
// table "Request Topics"; a link only carries the topic's code.
import { cleanParam, slug, REQUEST_FIELDS, LINK_FIELDS } from './requestOrigin.js';

export const FORM_TYPES = {
  team: { label: 'Equipo', note: 'Empresas y equipos comerciales (el formulario de siempre)' },
  individual: { label: 'Individual', note: 'Acompañamiento personal' },
  webinar: { label: 'Webinar', note: 'Inscripción a un webinar: la persona queda como invitada del evento' },
};
export const DEFAULT_FORM = 'team';
export const formTypeOf = (v) => (Object.prototype.hasOwnProperty.call(FORM_TYPES, v) ? v : DEFAULT_FORM);

// What each form asks for, as the server checks it (names after normalize() in request.mts)
export const REQUIRED_BY_FORM = {
  team: ['firstName', 'lastName', 'email', 'role', 'company', 'country', 'salesTeamSize', 'currentSalesMotion', 'mainChallenge', 'goals'],
  individual: ['firstName', 'lastName', 'email', 'country', 'situation', 'personalGoal'],
  webinar: ['firstName', 'lastName', 'email'],
};

// ── Airtable fields (docs/solicitudes/origen.md) ──
export const TOPIC_FIELDS = { name: 'Name', code: 'Code', active: 'Active' };
export const LINK_FORM_FIELDS = { formType: 'Form Type', topic: 'Topic Code', event: 'Link Event' };
export const REQUEST_FORM_FIELDS = { type: 'Request Type', topic: 'Topic Code', event: 'Request Event' };

const val = (fields, k) => { const v = fields?.[k]; return v && typeof v === 'object' && !Array.isArray(v) ? (v.name ?? '') : (v ?? ''); };
const first = (fields, k) => [].concat(fields?.[k] || [])[0] || null;

/**
 * When an event is, as text. Events › Starting is a date without time in
 * Airtable ("2026-11-12"): it is shown as that day, never moved by the time
 * zone (read as UTC midnight it would become the 11th in Latin America). A
 * value with a time shows the time too. public/home.html repeats this rule.
 */
export function eventWhen(startsAt, lang = 'es') {
  if (!startsAt) return '';
  const loc = lang === 'en' ? 'en' : 'es';
  try {
    if (/^\d{4}-\d{2}-\d{2}$/.test(String(startsAt))) return new Date(`${startsAt}T12:00:00Z`).toLocaleDateString(loc, { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC' });
    return new Date(startsAt).toLocaleString(loc, { weekday: 'long', day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' });
  } catch { return ''; }
}

/** A topic's code from its name: the same rules as any link parameter (short, no personal data). */
export const topicCode = (name) => cleanParam(slug(name));

/** The active topics, from the "Request Topics" records, in name order. */
export function activeTopics(records = []) {
  return records
    .map(r => ({ id: r.id, code: val(r.fields, TOPIC_FIELDS.code), name: val(r.fields, TOPIC_FIELDS.name), active: r.fields?.[TOPIC_FIELDS.active] === true }))
    .filter(t => t.code && t.name && t.active)
    .sort((a, b) => a.name.localeCompare(b.name, 'es'));
}

/** A topic's name for a code, or the code itself when the list no longer has it. */
export const topicName = (code, records = []) => (code ? (records.find(r => val(r.fields, TOPIC_FIELDS.code) === code)?.fields?.[TOPIC_FIELDS.name] || code) : '');

/**
 * What the request is for. An identified link decides (its kind, topic and
 * event): its topic was chosen from the list when the link was made, so it
 * stays even if the topic is archived later. Without a link, the URL's
 * oike_form and oike_topic, and then only an active topic: an unknown one is
 * dropped, never guessed.
 */
export function requestFormOf({ link = null, params = {}, topics = [] }) {
  const known = new Set(activeTopics(topics).map(t => t.code));
  const lf = link?.fields || {};
  const type = formTypeOf(link ? val(lf, LINK_FORM_FIELDS.formType) || DEFAULT_FORM : params.oike_form);
  const topic = link ? cleanParam(val(lf, LINK_FORM_FIELDS.topic)) || '' : (params.oike_topic && known.has(params.oike_topic) ? params.oike_topic : '');
  const eventId = type === 'webinar' && link ? first(lf, LINK_FORM_FIELDS.event) : null;
  return { type, topic, eventId };
}

/** The request fields for its kind, topic and event. */
export function requestFormFields({ type, topic, eventId }) {
  const f = { [REQUEST_FORM_FIELDS.type]: formTypeOf(type) };
  if (topic) f[REQUEST_FORM_FIELDS.topic] = topic;
  if (eventId) f[REQUEST_FORM_FIELDS.event] = [eventId];
  return f;
}

/** A request's kind (requests sent before kinds existed are team requests), topic and event. */
export function requestKindOf(fields = {}) {
  return { type: formTypeOf(val(fields, REQUEST_FORM_FIELDS.type) || DEFAULT_FORM), topic: val(fields, REQUEST_FORM_FIELDS.topic) || '', eventId: first(fields, REQUEST_FORM_FIELDS.event) };
}

// ── Requests that lost their kind, topic or webinar ──
// A request is always saved, but it is never shown as complete when what its
// link (or URL) asked for could not be recorded: missing Airtable fields, a
// topic outside the active list, a webinar link without its event, or an
// invitation that failed. Issues: 'type', 'topic', 'event', 'invitation'.
export const ISSUE_LABELS = { type: 'el tipo', topic: 'el tema', event: 'el webinar', invitation: 'la invitación al evento' };

/** At send time (request.mts), from what it knows directly. */
export function requestIssuesAtSend({ form, link, params = {}, kindSaved, invited }) {
  const issues = [];
  const askedTopic = link ? cleanParam(val(link.fields, LINK_FORM_FIELDS.topic)) : cleanParam(params.oike_topic);
  if (!kindSaved && form.type !== DEFAULT_FORM) issues.push('type');
  if (askedTopic && (!form.topic || !kindSaved)) issues.push('topic');
  if (form.type === 'webinar') {
    if (!form.eventId || !kindSaved) issues.push('event');
    else if (!invited) issues.push('invitation');
  }
  return issues;
}

/**
 * Afterwards (Solicitudes), from the stored request: what its parameters and
 * its link asked for, against what was recorded. `events` lets it check the
 * invitation; requests sent before kinds existed (no parameters) have no issues.
 */
export function requestIssuesOf(fields = {}, { links = [], events = [] } = {}) {
  let params = {};
  try { params = JSON.parse(val(fields, REQUEST_FIELDS.params) || '{}') || {}; } catch { params = {}; }
  const linkId = first(fields, REQUEST_FIELDS.link);
  const link = (linkId && links.find(l => l.id === linkId)) || (params.oike_link && links.find(l => val(l.fields, LINK_FIELDS.code) === params.oike_link)) || null;
  if (!link && !params.oike_form && !params.oike_topic) return [];
  const expectedType = link ? formTypeOf(val(link.fields, LINK_FORM_FIELDS.formType) || DEFAULT_FORM) : formTypeOf(params.oike_form);
  const expectedTopic = link ? cleanParam(val(link.fields, LINK_FORM_FIELDS.topic)) : cleanParam(params.oike_topic);
  const stored = requestKindOf(fields);
  const hasType = !!val(fields, REQUEST_FORM_FIELDS.type);
  const issues = [];
  if (expectedType !== DEFAULT_FORM && (!hasType || stored.type !== expectedType)) issues.push('type');
  if (expectedTopic && stored.topic !== expectedTopic) issues.push('topic');
  if (expectedType === 'webinar') {
    if (!stored.eventId) issues.push('event');
    else {
      const ev = events.find(e => e.id === stored.eventId);
      const person = first(fields, 'Stakeholder');
      if (ev && person && ![].concat(ev.fields?.['Stakeholders invited'] || []).includes(person)) issues.push('invitation');
    }
  }
  return issues;
}
